import { useState, useRef } from 'react'
import { fetchData } from '../../../../global/utils/fetch'
import { STATUS } from '../../../../global/types/audit'
import { evaluatePromptBatch } from './api'

interface UseTestResultsOptions {
  auditType: STATUS | null
  sectionKey: string | null
  promptText: string
  selectedModel: string
}

export function useTestResults({
  auditType,
  sectionKey,
  promptText,
  selectedModel,
}: UseTestResultsOptions) {
  const [isTestingPrompt, setIsTestingPrompt] = useState(false)
  const [testCount, setTestCount] = useState<number | ''>(5)
  const [currentPage, setCurrentPage] = useState(0)
  const [hasTestedBefore, setHasTestedBefore] = useState(false)
  const [lastTestSection, setLastTestSection] = useState<string | null>(null)
  const [lastTestCount, setLastTestCount] = useState<number | null>(null)
  const [testResults, setTestResults] = useState<any[] | null>(null)
  const [testSummary, setTestSummary] = useState<any | null>(null)
  const [tokenSummary, setTokenSummary] = useState<any | null>(null)
  const [testDateRange, setTestDateRange] = useState<{
    earliest: string
    latest: string
  } | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [correctedAudits, setCorrectedAudits] = useState<Set<string>>(new Set())

  const resultsRef = useRef<HTMLDivElement>(null)

  // Reset test state when section changes
  const resetTestState = () => {
    setHasTestedBefore(false)
    setCurrentPage(0)
    setShowResults(false)
    setTestResults(null)
    setTestSummary(null)
    setTokenSummary(null)
    setTestDateRange(null)
    setLastTestSection(null)
    setLastTestCount(null)
  }

  // Handler to mark a disagreement as "AI was correct"
  const handleMarkAICorrect = (auditObjectId: string) => {
    setCorrectedAudits((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(auditObjectId)) {
        newSet.delete(auditObjectId) // Toggle off if already corrected
      } else {
        newSet.add(auditObjectId) // Mark as corrected
      }
      return newSet
    })
  }

  // Test prompt against historical audits
  const handleTestPrompt = async (useNewAudits: boolean = true) => {
    if (!promptText) {
      alert('Please enter a prompt first')
      return
    }

    setIsTestingPrompt(true)
    try {
      // Determine which page to use
      const pageToUse = useNewAudits
        ? currentPage
        : Math.max(0, currentPage - 1)

      // Call consolidated batch endpoint
      const response = await fetchData<{
        summary: {
          total: number
          correct: number
          incorrect: number
          accuracy: number
        }
        audits: Array<Record<string, unknown>>
        tokenSummary: unknown
        dateRange: { earliest: string; latest: string } | null
      }>(evaluatePromptBatch({
        auditType,
        sectionKey,
        promptText,
        modelId: selectedModel,
        passCount: testCount,
        failCount: testCount,
        page: pageToUse,
      }))

      if (response.summary.total === 0) {
        alert('No test audits found for this section')
        return
      }

      // Store results, show section, and scroll to it
      setTestResults(response.audits)
      setTestSummary(response.summary)
      setTokenSummary(response.tokenSummary)
      setTestDateRange(response.dateRange || null)
      setShowResults(true)
      setCorrectedAudits(new Set()) // Reset corrections for new test

      // Mark that we've tested and track current section/count
      setHasTestedBefore(true)
      setLastTestSection(sectionKey)
      setLastTestCount(typeof testCount === 'number' ? testCount : null)

      // Increment page if using new audits
      if (useNewAudits) {
        setCurrentPage((prev) => prev + 1)
      }

      // Auto-scroll to results after a brief delay
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100)
    } catch (error) {
      console.error('Failed to test prompt:', error)
      alert('Failed to test prompt. Check console for details.')
    } finally {
      setIsTestingPrompt(false)
    }
  }

  return {
    // State
    isTestingPrompt,
    testCount,
    setTestCount,
    currentPage,
    hasTestedBefore,
    lastTestSection,
    lastTestCount,
    testResults,
    testSummary,
    tokenSummary,
    testDateRange,
    showResults,
    correctedAudits,
    resultsRef,

    // Actions
    handleTestPrompt,
    handleMarkAICorrect,
    resetTestState,
  }
}
